// src/pages/Warmup.tsx
import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container, Typography, Grid, Box, Paper, Chip, Alert, CircularProgress,
  FormControl, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton,
  Collapse, InputLabel, Tooltip
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import AccountPickerMulti from '../components/AccountPickerMulti';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit, PairRule } from '../hooks/useFieldValidation';

// ==== Границы полей: 1-в-1 из StartWarmupCommand (pydantic ge/le) ====
// Где в модели у кортежа нет ge/le — ставим разумный минимум без выдуманного потолка.
const WARMUP_LIMITS: Record<string, FieldLimit> = {
  durationDays: { min: 1, max: 30, integer: true, helper: '1–30 дней' },
  cyclesPerDay: { min: 1, max: 10, integer: true, helper: 'Сколько раз в день аккаунт будет активен' },

  searchChannelsCountMin: { min: 1, integer: true },
  searchChannelsCountMax: { min: 1, integer: true },
  subscribeCountMin: { min: 0, integer: true },
  subscribeCountMax: { min: 0, integer: true },
  interactionPerChannelMin: { min: 0, integer: true },
  interactionPerChannelMax: { min: 0, integer: true },

  interMessagesPerDayMin: { min: 0, integer: true },
  interMessagesPerDayMax: { min: 0, integer: true },
  interPrivateMessagesPerDayMin: { min: 0, integer: true },
  interPrivateMessagesPerDayMax: { min: 0, integer: true },

  actionDelayMin: { min: 0, integer: true },
  actionDelayMax: { min: 0, integer: true },
  cycleDelayMin: { min: 0, integer: true },
  cycleDelayMax: { min: 0, integer: true },
  stageDelayMin: { min: 0, integer: true },
  stageDelayMax: { min: 0, integer: true },
  betweenAccountsDelayMin: { min: 0, integer: true },
  betweenAccountsDelayMax: { min: 0, integer: true },

  progressiveStartActions: { min: 1, max: 50, integer: true },
  progressiveMaxActions: { min: 1, max: 100, integer: true },
  progressiveDailyIncrease: { min: 1, max: 20, integer: true },

  workingHoursStart: { min: 0, max: 24, step: 0.5 },
  workingHoursStop: { min: 0, max: 24, step: 0.5 },

  maxSubscriptionsDay: { min: 1, max: 20, integer: true },
  maxReactionsDay: { min: 1, max: 100, integer: true },
  maxCommentsDay: { min: 1, max: 30, integer: true },
  maxMessagesDay: { min: 1, max: 50, integer: true },
};

const WARMUP_PAIRS: PairRule[] = [
  { minKey: 'searchChannelsCountMin', maxKey: 'searchChannelsCountMax' },
  { minKey: 'subscribeCountMin', maxKey: 'subscribeCountMax' },
  { minKey: 'interactionPerChannelMin', maxKey: 'interactionPerChannelMax' },
  { minKey: 'interMessagesPerDayMin', maxKey: 'interMessagesPerDayMax' },
  { minKey: 'interPrivateMessagesPerDayMin', maxKey: 'interPrivateMessagesPerDayMax' },
  { minKey: 'actionDelayMin', maxKey: 'actionDelayMax' },
  { minKey: 'cycleDelayMin', maxKey: 'cycleDelayMax' },
  { minKey: 'stageDelayMin', maxKey: 'stageDelayMax' },
  { minKey: 'betweenAccountsDelayMin', maxKey: 'betweenAccountsDelayMax' },
  { minKey: 'workingHoursStart', maxKey: 'workingHoursStop', message: 'Конец должен быть позже начала' },
  { minKey: 'progressiveStartActions', maxKey: 'progressiveMaxActions', message: 'Максимум должен быть ≥ начального' },
];

interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveCampaign {
  campaign_id: string;
  status: string;
  account_names: string[];
  accounts_count: number;
  current_day: number;
  total_days: number;
  current_stage: string;
  current_cycle: number;
  total_actions: number;
  errors: number;
  warnings: string[];
}

const Warmup: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [campaigns, setCampaigns] = useState<ActiveCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showProgressive, setShowProgressive] = useState(false);
  const [showInterAccount, setShowInterAccount] = useState(false);
  const [showAiComments, setShowAiComments] = useState(false);
  const [showWorkingHours, setShowWorkingHours] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const [showStages, setShowStages] = useState(false);
  const [showDescriptionDetails, setShowDescriptionDetails] = useState(false);

  const [form, setForm] = useState({
    accountNames: [] as string[],
    durationDays: '7',
    cyclesPerDay: '3',
    stages: {
      stage_1_basic: true,
      stage_2_social: true,
      stage_3_communication: true,
      stage_4_group: true,
    },
    channelsMode: 'search' as 'from_file' | 'manual' | 'search' | 'combined',
    channelsFile: null as File | null,
    manualChannels: '',
    searchKeywords: '',
    searchChannelsCountMin: '3',
    searchChannelsCountMax: '5',
    searchAlsoGroups: true,
    subscribeCountMin: '5',
    subscribeCountMax: '10',
    interactionPerChannelMin: '2',
    interactionPerChannelMax: '5',
    interAccountEnabled: true,
    createInterGroup: true,
    interGroupName: '',
    interMessagesPerDayMin: '5',
    interMessagesPerDayMax: '15',
    interUseAi: true,
    interConversationTopics: '',
    interPrivateMessages: false,
    interPrivateMessagesPerDayMin: '2',
    interPrivateMessagesPerDayMax: '5',
    useAiComments: true,
    aiPromptTemplate: '',
    aiStyle: 'mixed' as 'casual' | 'formal' | 'emotional' | 'mixed',
    aiLength: 'mixed' as 'short' | 'medium' | 'long' | 'mixed',
    aiUseEmoji: true,
    actionDelayMin: '30',
    actionDelayMax: '120',
    cycleDelayMin: '300',
    cycleDelayMax: '900',
    stageDelayMin: '7200',
    stageDelayMax: '21600',
    betweenAccountsDelayMin: '5',
    betweenAccountsDelayMax: '15',
    progressiveIncrease: true,
    progressiveStartActions: '5',
    progressiveMaxActions: '30',
    progressiveDailyIncrease: '5',
    workingHoursEnabled: true,
    workingHoursStart: '8.0',
    workingHoursStop: '22.0',
    workingHoursTimezone: 'Europe/Moscow',
    maxSubscriptionsDay: '7',
    maxReactionsDay: '30',
    maxCommentsDay: '8',
    maxMessagesDay: '15',
    checkSpamBlock: true,
    rotateProxyOnError: true,
    stopOnBan: true,
  });

  const update = (key: keyof typeof form, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  const validation = useFieldValidation({ limits: WARMUP_LIMITS, pairs: WARMUP_PAIRS });

  const updateStage = (stage: keyof typeof form.stages, value: boolean) => {
    setForm(prev => ({
      ...prev,
      stages: { ...prev.stages, [stage]: value },
    }));
  };

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchActive();
    const interval = setInterval(fetchActive, 8000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const accRes = await api.get('/accounts');
      setAccounts(accRes.data.accounts || []);
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
    } catch (err: any) {
      setMessage({ text: 'Ошибка загрузки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/warmup/list');
      setCampaigns(res.data.campaigns || []);
    } catch (err) {
      console.error(err);
    }
  };

  const startCampaign = async () => {
  if (form.accountNames.length === 0) {
    setMessage({ text: 'Выберите аккаунты', type: 'error' });
    return;
  }

  if (!validation.validateAll(form)) {
    setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
    return;
  }

  const formData = new FormData();

  // Аккаунты
  form.accountNames.forEach(name => formData.append('account_names', name));

  // Основные параметры
  formData.append('duration_days', form.durationDays);
  formData.append('cycles_per_day', form.cyclesPerDay);


 formData.append('stages', JSON.stringify(form.stages));

  // Каналы
  formData.append('channels_mode', form.channelsMode);

  if (form.channelsFile) {
    formData.append('channels_file', form.channelsFile);
  }

  // manual_channels — отдельные append для каждого
  if (form.manualChannels.trim()) {
    form.manualChannels.split(',').map(s => s.trim()).filter(Boolean).forEach(ch => {
      formData.append('manual_channels', ch);
    });
  }

  // search_keywords — отдельные append
  if (form.searchKeywords.trim()) {
    form.searchKeywords.split(',').map(s => s.trim()).filter(Boolean).forEach(kw => {
      formData.append('search_keywords', kw);
    });
  }

  // Все кортежи — по два отдельных append (min и max)
  formData.append('search_channels_count', form.searchChannelsCountMin);
  formData.append('search_channels_count', form.searchChannelsCountMax);

  formData.append('search_also_groups', form.searchAlsoGroups.toString());

  formData.append('subscribe_count', form.subscribeCountMin);
  formData.append('subscribe_count', form.subscribeCountMax);

  formData.append('interaction_per_channel', form.interactionPerChannelMin);
  formData.append('interaction_per_channel', form.interactionPerChannelMax);

  // Общение
  formData.append('inter_account_enabled', form.interAccountEnabled.toString());

  if (form.interAccountEnabled) {
    formData.append('create_inter_group', form.createInterGroup.toString());
    if (form.interGroupName.trim()) formData.append('inter_group_name', form.interGroupName);

    formData.append('inter_messages_per_day', form.interMessagesPerDayMin);
    formData.append('inter_messages_per_day', form.interMessagesPerDayMax);

    formData.append('inter_use_ai', form.interUseAi.toString());

    if (form.interConversationTopics.trim()) {
      form.interConversationTopics.split(',').map(s => s.trim()).filter(Boolean).forEach(topic => {
        formData.append('inter_conversation_topics', topic);
      });
    }

    formData.append('inter_private_messages', form.interPrivateMessages.toString());

    if (form.interPrivateMessages) {
      formData.append('inter_private_messages_per_day', form.interPrivateMessagesPerDayMin);
      formData.append('inter_private_messages_per_day', form.interPrivateMessagesPerDayMax);
    }
  }

  // AI комментарии
  formData.append('use_ai_comments', form.useAiComments.toString());

  if (form.useAiComments) {
    if (form.aiPromptTemplate.trim()) formData.append('ai_prompt_template', form.aiPromptTemplate);
    formData.append('ai_style', form.aiStyle);
    formData.append('ai_length', form.aiLength);
    formData.append('ai_use_emoji', form.aiUseEmoji.toString());
  }

  // Задержки — по два append
  formData.append('action_delay', form.actionDelayMin);
  formData.append('action_delay', form.actionDelayMax);

  formData.append('cycle_delay', form.cycleDelayMin);
  formData.append('cycle_delay', form.cycleDelayMax);

  formData.append('stage_delay', form.stageDelayMin);
  formData.append('stage_delay', form.stageDelayMax);

  formData.append('between_accounts_delay', form.betweenAccountsDelayMin);
  formData.append('between_accounts_delay', form.betweenAccountsDelayMax);

  // Прогрессивное увеличение
  formData.append('progressive_increase', form.progressiveIncrease.toString());

  if (form.progressiveIncrease) {
    formData.append('progressive_start_actions', form.progressiveStartActions);
    formData.append('progressive_max_actions', form.progressiveMaxActions);
    formData.append('progressive_daily_increase', form.progressiveDailyIncrease);
  }

  // Рабочие часы
  formData.append('working_hours_enabled', form.workingHoursEnabled.toString());

  if (form.workingHoursEnabled) {
    formData.append('working_hours_start', form.workingHoursStart);
    formData.append('working_hours_stop', form.workingHoursStop);
    formData.append('working_hours_timezone', form.workingHoursTimezone);
  }

  // Безопасность
  formData.append('max_subscriptions_day', form.maxSubscriptionsDay);
  formData.append('max_reactions_day', form.maxReactionsDay);
  formData.append('max_comments_day', form.maxCommentsDay);
  formData.append('max_messages_day', form.maxMessagesDay);
  formData.append('check_spam_block', form.checkSpamBlock.toString());
  formData.append('rotate_proxy_on_error', form.rotateProxyOnError.toString());
  formData.append('stop_on_ban', form.stopOnBan.toString());

  setIsLoading(true);
  try {
    await api.post('/warmup/start', formData);
    setMessage({ text: 'Прогрев запущен!', type: 'success' });
    setTab('active');
    fetchActive();
  } catch (err: any) {
    setMessage({ text: err.response?.data?.detail || 'Ошибка запуска', type: 'error' });
  } finally {
    setIsLoading(false);
  }
};

  const stopCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/warmup/stop/${id}`);
      setMessage({ text: 'Кампания остановлена', type: 'info' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка остановки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const restartCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      await api.get(`/warmup/restart/${id}`);
      setMessage({ text: 'Кампания перезапущена', type: 'success' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка перезапуска', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCampaign = async (id: string) => {
    if (!window.confirm('Удалить кампанию навсегда?')) return;
    setIsLoading(true);
    try {
      await api.get(`/warmup/delete/${id}`);
      setMessage({ text: 'Кампания удалена', type: 'success' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка удаления', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* Заголовок */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <WhatshotIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Прогрев аккаунтов
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Поэтапный прогрев" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="AI комментарии, подписки и просмотры" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Общение между аккаунтами" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Автоматический прогрев новых аккаунтов для снижения риска бана.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Описание с "Подробнее" */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работает прогрев аккаунтов?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Аккаунты постепенно увеличивают активность, имитируя поведение реального пользователя.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Этапы</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Базовая активность, социальные действия, коммуникация, групповая активность.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Общение</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Между аккаунтами в группе или личных сообщениях.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Безопасность</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Прогрессивное увеличение, рабочие часы, лимиты.
            </Typography>
          </Box>

        </Box>

        <Button variant="outlined" fullWidth onClick={() => setShowDescriptionDetails(!showDescriptionDetails)} endIcon={showDescriptionDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
          Подробнее
        </Button>

        <Collapse in={showDescriptionDetails}>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ color: '#4B5563', lineHeight: 1.7 }}>
              Прогрев — это система имитации естественной активности для новых или неактивных аккаунтов. Она помогает аккаунту выглядеть живым, снижая риск блокировки при использовании в массовых автоматизациях.
            </Typography>
            <Typography sx={{ color: '#4B5563', lineHeight: 1.7, mt: 2 }}>
              Процесс разделён на дни и циклы. В каждый день аккаунт выполняет несколько циклов активности. Активность растёт постепенно, если включена прогрессия.
            </Typography>
            <Typography sx={{ color: '#4B5563', lineHeight: 1.7, mt: 2 }}>
              Аккаунты могут общаться между собой в общей группе или личных сообщениях, что создаёт историю переписки.
            </Typography>
            <Typography sx={{ color: '#4B5563', lineHeight: 1.7, mt: 2 }}>
              Каналы для подписки можно указать вручную, из файла или найти по ключевым словам. Аккаунт взаимодействует с постами: ставит реакции, пишет комментарии.
            </Typography>
            <Typography sx={{ color: '#4B5563', lineHeight: 1.7, mt: 2 }}>
              Безопасность обеспечивается задержками, лимитами и проверками. Рабочие часы имитируют режим реального человека.
            </Typography>
            <Typography sx={{ color: '#4B5563', lineHeight: 1.7, mt: 2 }}>
              Этапы можно включать/выключать. Рекомендуется начинать с минимальной активности и постепенно увеличивать.
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 3, color: '#1E40AF' }}>Рекомендации</Typography>
            <Typography sx={{ color: '#4B5563', lineHeight: 1.7, mt: 1 }}>
              Включайте прогрессивное увеличение, используйте рабочие часы, включайте AI для комментариев, выбирайте 7+ дней для нового аккаунта.
            </Typography>
            <Typography variant="subtitle1" sx={{mt: 3, color: '#1E40AF'}}>
              FAQ
            </Typography>

            <Typography sx={{color: '#4B5563', lineHeight: 1.7, mt: 1}}>
              <Box component="span" sx={{fontWeight: 600}}>
                Сколько аккаунтов можно прогревать одновременно?
              </Box>{' '}
              Рекомендуется от 2 до 5.
            </Typography>

            <Typography sx={{color: '#4B5563', lineHeight: 1.7}}>
              <Box component="span" sx={{fontWeight: 600}}>
                Можно ли использовать аккаунт во время прогрева?
              </Box>{' '}
              Нет, аккаунт заблокирован для других задач.
            </Typography>

            <Typography sx={{color: '#4B5563', lineHeight: 1.7}}>
              <Box component="span" sx={{fontWeight: 600}}>
                Что если прогрев прервётся?
              </Box>{' '}
              Система автоматически восстановит его прогрев с того же места.
            </Typography>

            <Typography sx={{color: '#4B5563', lineHeight: 1.7}}>
              <Box component="span" sx={{fontWeight: 600}}>
                Нужно ли прогревать старые аккаунты?
              </Box>{' '}
              Если неактивен 2+ месяца — да, 3-5 дней.
            </Typography>
          </Box>
        </Collapse>
        <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Прогревайте свои аккаунты и улучшайте их жизнеспособность для использования в наших автоматизациях
        </Typography>
            <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/warmups"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
          </Typography>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => setTab('setup')} sx={getTabStyle('setup')} startIcon={<PlayArrowIcon />}>
            Новая кампания
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои прогревы ({campaigns.length})
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          {tab === 'setup' && (
  <Grid container spacing={3}>
    {/* Аккаунты */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom>
        Аккаунты
        <Tooltip title="Выберите аккаунты для прогрева">
          <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8' }} />
        </Tooltip>
      </Typography>
      <AccountPickerMulti
        accounts={accounts}
        selected={form.accountNames}
        onChange={(names) => update('accountNames', names)}
        groups={groups}
      />
    </Grid>

    <Grid size={6}>
      <TextField
        label="Длительность (дни)"
        type="number"
        {...validation.fieldProps('durationDays', form, update)}
        fullWidth
      />
    </Grid>

    <Grid size={6}>
      <TextField
        label="Циклов в день"
        type="number"
        {...validation.fieldProps('cyclesPerDay', form, update)}
        fullWidth
      />
    </Grid>

    {/* Этапы прогрева — всегда видно */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
        Этапы прогрева
      </Typography>
      <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #DBEAFE' }}>
        <FormControlLabel
          control={<Checkbox checked={form.stages.stage_1_basic} onChange={e => updateStage('stage_1_basic', e.target.checked)} />}
          label="Stage 1: Базовая активность"
        />
        <FormControlLabel
          control={<Checkbox checked={form.stages.stage_2_social} onChange={e => updateStage('stage_2_social', e.target.checked)} />}
          label="Stage 2: Социальные действия"
        />
        <FormControlLabel
          control={<Checkbox checked={form.stages.stage_3_communication} onChange={e => updateStage('stage_3_communication', e.target.checked)} />}
          label="Stage 3: Коммуникация"
        />
        <FormControlLabel
          control={<Checkbox checked={form.stages.stage_4_group} onChange={e => updateStage('stage_4_group', e.target.checked)} />}
          label="Stage 4: Групповая активность"
        />
      </Box>
    </Grid>

    {/* Режим каналов */}
    <Grid size={12}>
      <FormControl fullWidth>
        <InputLabel>Режим каналов</InputLabel>
        <Select
          value={form.channelsMode}
          onChange={e => update('channelsMode', e.target.value as any)}
          label="Режим каналов"
        >
          <MenuItem value="search">Поиск по словам</MenuItem>
          <MenuItem value="from_file">Из файла</MenuItem>
          <MenuItem value="manual">Вручную</MenuItem>
          <MenuItem value="combined">Комбинированный</MenuItem>
        </Select>
      </FormControl>
    </Grid>

    {/* Файл каналов */}
    {['from_file', 'combined'].includes(form.channelsMode) && (
      <Grid size={12}>
        <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #CBD5E1' }}>
          <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
            Файл с каналами (.txt)
            <input
              type="file"
              hidden
              accept=".txt"
              onChange={(e: ChangeEvent<HTMLInputElement>) => e.target.files?.[0] && update('channelsFile', e.target.files[0])}
            />
          </Button>
          {form.channelsFile && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFileIcon color="primary" />
              <Typography variant="body2">{form.channelsFile.name}</Typography>
              <IconButton size="small" onClick={() => update('channelsFile', null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>
    )}

    {/* Каналы вручную */}
    {['manual', 'combined'].includes(form.channelsMode) && (
      <Grid size={12}>
        <TextField
          label="Каналы вручную (через запятую)"
          value={form.manualChannels}
          onChange={e => update('manualChannels', e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder="@channel1, https://t.me/channel2, -1001234567890"
        />
      </Grid>
    )}

    {/* Поиск по словам */}
    {['search', 'combined'].includes(form.channelsMode) && (
      <>
        <Grid size={12}>
          <TextField
            label="Ключевые слова для поиска (через запятую)"
            value={form.searchKeywords}
            onChange={e => update('searchKeywords', e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="javascript, программирование, разработка"
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label="Каналов на слово мин"
            type="number"
            {...validation.fieldProps('searchChannelsCountMin', form, update)}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label="Каналов на слово макс"
            type="number"
            {...validation.fieldProps('searchChannelsCountMax', form, update)}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={<Checkbox checked={form.searchAlsoGroups} onChange={e => update('searchAlsoGroups', e.target.checked)} />}
            label="Искать также группы"
          />
        </Grid>
      </>
    )}

    {/* Подписки и взаимодействия */}
    <Grid size={6}>
      <TextField
        label="Подписок мин"
        type="number"
        {...validation.fieldProps('subscribeCountMin', form, update)}
        fullWidth
      />
    </Grid>
    <Grid size={6}>
      <TextField
        label="Подписок макс"
        type="number"
        {...validation.fieldProps('subscribeCountMax', form, update)}
        fullWidth
      />
    </Grid>

    <Grid size={6}>
      <TextField
        label="Взаимодействий на канал мин"
        type="number"
        {...validation.fieldProps('interactionPerChannelMin', form, update)}
        fullWidth
      />
    </Grid>
    <Grid size={6}>
      <TextField
        label="Взаимодействий на канал макс"
        type="number"
        {...validation.fieldProps('interactionPerChannelMax', form, update)}
        fullWidth
      />
    </Grid>

    {/* Общение между аккаунтами — всегда видно */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
        Общение между аккаунтами
      </Typography>
      <FormControlLabel
        control={<Checkbox checked={form.interAccountEnabled} onChange={e => update('interAccountEnabled', e.target.checked)} />}
        label="Включить общение между аккаунтами"
      />
      {form.interAccountEnabled && (
        <Box sx={{ pl: 4, mt: 2, bgcolor: '#F8FAFC', p: 3, borderRadius: 2, border: '1px solid #DBEAFE' }}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControlLabel
                control={<Checkbox checked={form.createInterGroup} onChange={e => update('createInterGroup', e.target.checked)} />}
                label="Создать общую группу"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Название группы (опционально)"
                value={form.interGroupName}
                onChange={e => update('interGroupName', e.target.value)}
                fullWidth
                placeholder="Оставьте пустым — будет случайное"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Сообщений в день мин"
                type="number"
                {...validation.fieldProps('interMessagesPerDayMin', form, update)}
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Сообщений в день макс"
                type="number"
                {...validation.fieldProps('interMessagesPerDayMax', form, update)}
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={<Checkbox checked={form.interUseAi} onChange={e => update('interUseAi', e.target.checked)} />}
                label="Использовать AI для генерации сообщений"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Темы для обсуждения (через запятую)"
                value={form.interConversationTopics}
                onChange={e => update('interConversationTopics', e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="программирование, новости, технологии"
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={<Checkbox checked={form.interPrivateMessages} onChange={e => update('interPrivateMessages', e.target.checked)} />}
                label="Отправлять также личные сообщения"
              />
            </Grid>
            {form.interPrivateMessages && (
              <>
                <Grid size={6}>
                  <TextField
                    label="Личных сообщений мин"
                    type="number"
                    {...validation.fieldProps('interPrivateMessagesPerDayMin', form, update)}
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    label="Личных сообщений макс"
                    type="number"
                    {...validation.fieldProps('interPrivateMessagesPerDayMax', form, update)}
                    fullWidth
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}
    </Grid>

    {/* AI комментарии — всегда видно */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
        AI комментарии в каналах
      </Typography>
      <FormControlLabel
        control={<Checkbox checked={form.useAiComments} onChange={e => update('useAiComments', e.target.checked)} />}
        label="Использовать AI для комментариев"
      />
      {form.useAiComments && (
        <Box sx={{ pl: 4, mt: 2, bgcolor: '#F8FAFC', p: 3, borderRadius: 2, border: '1px solid #DBEAFE' }}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                label="Шаблон промпта AI (опционально)"
                value={form.aiPromptTemplate}
                onChange={e => update('aiPromptTemplate', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Напиши естественный комментарий к посту о..."
              />
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Стиль</InputLabel>
                <Select value={form.aiStyle} onChange={e => update('aiStyle', e.target.value as any)} label="Стиль">
                  <MenuItem value="mixed">Смешанный</MenuItem>
                  <MenuItem value="casual">Неформальный</MenuItem>
                  <MenuItem value="formal">Формальный</MenuItem>
                  <MenuItem value="emotional">Эмоциональный</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Длина</InputLabel>
                <Select value={form.aiLength} onChange={e => update('aiLength', e.target.value as any)} label="Длина">
                  <MenuItem value="mixed">Смешанная</MenuItem>
                  <MenuItem value="short">Короткая</MenuItem>
                  <MenuItem value="medium">Средняя</MenuItem>
                  <MenuItem value="long">Длинная</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={<Checkbox checked={form.aiUseEmoji} onChange={e => update('aiUseEmoji', e.target.checked)} />}
                label="Использовать эмодзи в комментариях"
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Grid>

    {/* Поведение и задержки — всегда видно */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
        Поведение и задержки
      </Typography>
      <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #DBEAFE' }}>
        <Grid container spacing={3}>
          <Grid size={6}>
            <TextField
              label="Задержка действий мин (сек)"
              type="number"
              {...validation.fieldProps('actionDelayMin', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Задержка действий макс (сек)"
              type="number"
              {...validation.fieldProps('actionDelayMax', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Задержка циклов мин (сек)"
              type="number"
              {...validation.fieldProps('cycleDelayMin', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Задержка циклов макс (сек)"
              type="number"
              {...validation.fieldProps('cycleDelayMax', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Задержка этапов мин (сек)"
              type="number"
              {...validation.fieldProps('stageDelayMin', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Задержка этапов макс (сек)"
              type="number"
              {...validation.fieldProps('stageDelayMax', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Задержка между аккаунтами мин (сек)"
              type="number"
              {...validation.fieldProps('betweenAccountsDelayMin', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Задержка между аккаунтами макс (сек)"
              type="number"
              {...validation.fieldProps('betweenAccountsDelayMax', form, update)}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
    </Grid>

    {/* Прогрессивное увеличение — всегда видно */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
        Прогрессивное увеличение
      </Typography>
      <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #DBEAFE' }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <FormControlLabel
              control={<Checkbox checked={form.progressiveIncrease} onChange={e => update('progressiveIncrease', e.target.checked)} />}
              label="Включить прогрессивное увеличение активности"
            />
          </Grid>
          {form.progressiveIncrease && (
            <>
              <Grid size={12}>
                <TextField
                  label="Начальные действия в день"
                  type="number"
                  {...validation.fieldProps('progressiveStartActions', form, update)}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Максимум действий в день"
                  type="number"
                  {...validation.fieldProps('progressiveMaxActions', form, update)}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Ежедневное увеличение"
                  type="number"
                  {...validation.fieldProps('progressiveDailyIncrease', form, update)}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Grid>

    {/* Рабочие часы — всегда видно */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
        Рабочие часы
      </Typography>
      <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #DBEAFE' }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <FormControlLabel
              control={<Checkbox checked={form.workingHoursEnabled} onChange={e => update('workingHoursEnabled', e.target.checked)} />}
              label="Ограничить рабочие часы"
            />
          </Grid>
          {form.workingHoursEnabled && (
            <>
              <Grid size={6}>
                <TextField
                  label="Начало (час)"
                  type="number"
                  {...validation.fieldProps('workingHoursStart', form, update)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Конец (час)"
                  type="number"
                  {...validation.fieldProps('workingHoursStop', form, update)}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Часовой пояс"
                  value={form.workingHoursTimezone}
                  onChange={e => update('workingHoursTimezone', e.target.value)}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Grid>

    {/* Безопасность — всегда видно */}
    <Grid size={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1E40AF' }}>
        Безопасность и лимиты
      </Typography>
      <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #DBEAFE' }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <TextField
              label="Макс подписок в день"
              type="number"
              {...validation.fieldProps('maxSubscriptionsDay', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Макс реакций в день"
              type="number"
              {...validation.fieldProps('maxReactionsDay', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Макс комментариев в день"
              type="number"
              {...validation.fieldProps('maxCommentsDay', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Макс сообщений в день"
              type="number"
              {...validation.fieldProps('maxMessagesDay', form, update)}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={<Checkbox checked={form.checkSpamBlock} onChange={e => update('checkSpamBlock', e.target.checked)} />}
              label="Проверять блокировки за спам"
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={<Checkbox checked={form.rotateProxyOnError} onChange={e => update('rotateProxyOnError', e.target.checked)} />}
              label="Ротировать прокси при ошибках"
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={<Checkbox checked={form.stopOnBan} onChange={e => update('stopOnBan', e.target.checked)} />}
              label="Остановить прогрев при бане"
            />
          </Grid>
        </Grid>
      </Box>
    </Grid>

    {/* Кнопка запуска */}
    <Grid size={12}>
      <Button
        variant="contained"
        onClick={startCampaign}
        disabled={isLoading || form.accountNames.length === 0 || validation.hasErrors}
        startIcon={isLoading ? <CircularProgress size={22} /> : <PlayArrowIcon />}
        fullWidth
        sx={{ py: 2, fontSize: '1.1rem', bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
      >
        {isLoading ? 'Запуск...' : 'Запустить прогрев'}
      </Button>
    </Grid>
  </Grid>
)}

          {tab === 'active' && (
            campaigns.length === 0 ? (
              <Typography textAlign="center" py={10} color="text.secondary">
                Нет активных прогревов
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Аккаунты</strong></TableCell>
                      <TableCell><strong>День</strong></TableCell>
                      <TableCell><strong>Этап</strong></TableCell>
                      <TableCell><strong>Действия</strong></TableCell>
                      <TableCell><strong>Ошибки</strong></TableCell>
                      <TableCell><strong>Статус</strong></TableCell>
                      <TableCell align="right"><strong>Действия</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map(c => (
                      <TableRow key={c.campaign_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/warmup/detail/${c.campaign_id}`)}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{c.campaign_id.slice(0, 8)}</TableCell>
                        <TableCell>{c.account_names.length}</TableCell>
                        <TableCell>{c.current_day}/{c.total_days}</TableCell>
                        <TableCell>{c.current_stage}</TableCell>
                        <TableCell>{c.total_actions}</TableCell>
                        <TableCell sx={{ color: c.errors > 0 ? '#DC2626' : 'inherit' }}>{c.errors}</TableCell>
                          <TableCell>
                              <Chip
                                  label={
                                      c.status === 'running' ? 'Работает' :
                                          c.status === 'completed' ? 'Завершён' :
                                              c.status === 'stopped' ? 'Остановлен' :
                                                  c.status === 'paused' ? 'На паузе' :
                                                      c.status === 'error' ? 'Ошибка' :
                                                          c.status  // fallback — если неизвестный статус
                                  }
                                  size="small"
                                  color={
                                      c.status === 'running' ? 'success' :
                                          c.status === 'completed' ? 'success' :
                                              c.status === 'stopped' || c.status === 'paused' ? 'warning' :
                                                  c.status === 'error' ? 'error' :
                                                      'default'
                                  }
                                  sx={{minWidth: 100}}
                              />
                          </TableCell>
                        <TableCell align="right" onClick={e => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {c.status === 'running' && (
                              <IconButton size="small" color="error" onClick={() => stopCampaign(c.campaign_id)}>
                                <StopIcon />
                              </IconButton>
                            )}
                            {c.status !== 'running' && (
                              <IconButton size="small" color="success" onClick={() => restartCampaign(c.campaign_id)}>
                                <PlayArrowIcon />
                              </IconButton>
                            )}
                            <IconButton size="small" color="error" onClick={() => deleteCampaign(c.campaign_id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Warmup;